import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import Cropper from "react-easy-crop";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Download,
  FileText,
  Image as ImageIcon,
  Maximize2,
  Minimize2,
  Printer,
  RotateCcw,
  Upload,
} from "lucide-react";
import { ADDRESS_DATA, getBarangays } from "../../src/data/luzonAddressData";
import { unionAffiliationData } from "../../src/data/employmentOptions";
import { fileToDataUrl, getCroppedImageDataUrl } from "../../src/utils/imageCrop";
import { BASE_URL } from "../../src/api/client";
import "../styles/membership-form.css";
import "../styles/membership-form-wizard.css";

const PHOTO_CROP_FRAME_RATIO = 1;
const MOBILE_SIGNATURE_BREAKPOINT = 768;

const computeMinimumCropZoom = (width, height) => {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return 1;
  }

  const aspectRatio = width / height;
  const zoomRequired = PHOTO_CROP_FRAME_RATIO * aspectRatio;
  return Number(Math.max(1, zoomRequired).toFixed(2));
};

// --- Helper Components ---

function FormSection({ title, children, isActive }) {
  if (!isActive) return null;
  return (
    <section className="membership-form__section animate-fade-in">
      <div className="membership-form__section-banner">{title}</div>
      {children}
    </section>
  );
}

function SignaturePad({ onSave, disabled }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isPadMaximized, setIsPadMaximized] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= MOBILE_SIGNATURE_BREAKPOINT;
  });

  const updateViewportState = useCallback(() => {
    if (typeof window === "undefined") return;
    setIsMobileViewport(window.innerWidth <= MOBILE_SIGNATURE_BREAKPOINT);
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wrapper = canvas.parentElement;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const nextWidth = Math.max(1, Math.floor(rect.width));
    const nextHeight = Math.max(1, Math.floor(rect.height));
    const dpr = Math.max(window.devicePixelRatio || 1, 1);

    if (canvas.width === Math.floor(nextWidth * dpr) && canvas.height === Math.floor(nextHeight * dpr)) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.strokeStyle = "#000";
      }
      return;
    }

    const snapshot = hasSignature ? canvas.toDataURL("image/png") : null;

    canvas.width = Math.floor(nextWidth * dpr);
    canvas.height = Math.floor(nextHeight * dpr);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Scale the drawing context so pointer coordinates continue using CSS pixels.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";

    if (snapshot) {
      const image = new Image();
      image.onload = () => {
        ctx.drawImage(image, 0, 0, nextWidth, nextHeight);
      };
      image.src = snapshot;
    }
  }, [hasSignature]);

  useEffect(() => {
    updateViewportState();
    resizeCanvas();

    window.addEventListener("resize", updateViewportState);
    window.addEventListener("resize", resizeCanvas);
    return () => {
      window.removeEventListener("resize", updateViewportState);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [updateViewportState, resizeCanvas]);

  useEffect(() => {
    resizeCanvas();
  }, [isMobileViewport, isPadMaximized, resizeCanvas]);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsPadMaximized(false);
    }
  }, [isMobileViewport]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDrawing = (e) => {
    if (isConfirmed || disabled) return;
    if (e.cancelable) e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || isConfirmed || disabled) return;
    if (e.cancelable) e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasSignature) setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const exportNormalizedSignature = () => {
    const sourceCanvas = canvasRef.current;
    if (!sourceCanvas) return null;

    const srcCtx = sourceCanvas.getContext("2d");
    if (!srcCtx) return sourceCanvas.toDataURL();

    const { width: srcWidth, height: srcHeight } = sourceCanvas;
    const imageData = srcCtx.getImageData(0, 0, srcWidth, srcHeight).data;

    let minX = srcWidth;
    let minY = srcHeight;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < srcHeight; y += 1) {
      for (let x = 0; x < srcWidth; x += 1) {
        const alpha = imageData[(y * srcWidth + x) * 4 + 3];
        if (alpha > 0) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < minX || maxY < minY) {
      return sourceCanvas.toDataURL();
    }

    const inkWidth = maxX - minX + 1;
    const inkHeight = maxY - minY + 1;

    const cropCanvas = document.createElement("canvas");
    cropCanvas.width = inkWidth;
    cropCanvas.height = inkHeight;
    const cropCtx = cropCanvas.getContext("2d");
    if (!cropCtx) return sourceCanvas.toDataURL();
    cropCtx.drawImage(sourceCanvas, minX, minY, inkWidth, inkHeight, 0, 0, inkWidth, inkHeight);

    const shouldRotateForMobile = isMobileViewport && inkHeight > inkWidth;
    let orientedCanvas = cropCanvas;

    if (shouldRotateForMobile) {
      const rotatedCanvas = document.createElement("canvas");
      rotatedCanvas.width = inkHeight;
      rotatedCanvas.height = inkWidth;
      const rotatedCtx = rotatedCanvas.getContext("2d");
      if (rotatedCtx) {
        rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
        rotatedCtx.rotate(-Math.PI / 2);
        rotatedCtx.drawImage(cropCanvas, -inkWidth / 2, -inkHeight / 2);
        orientedCanvas = rotatedCanvas;
      }
    }

    const outputCanvas = document.createElement("canvas");
    outputCanvas.width = 600;
    outputCanvas.height = 240;
    const outputCtx = outputCanvas.getContext("2d");
    if (!outputCtx) return sourceCanvas.toDataURL();

    const padding = 30;
    const availWidth = outputCanvas.width - padding * 2;
    const availHeight = outputCanvas.height - padding * 2;
    const fittedScale = Math.min(
      availWidth / orientedCanvas.width,
      availHeight / orientedCanvas.height
    );
    const scale = fittedScale * 0.5;
    const drawWidth = orientedCanvas.width * scale;
    const drawHeight = orientedCanvas.height * scale;
    const drawX = (outputCanvas.width - drawWidth) / 2;
    const drawY = (outputCanvas.height - drawHeight) / 2;

    outputCtx.drawImage(orientedCanvas, drawX, drawY, drawWidth, drawHeight);
    return outputCanvas.toDataURL();
  };

  const handleConfirm = () => {
    if (disabled) return;
    if (hasSignature) {
      setIsConfirmed(true);
      onSave(exportNormalizedSignature());
    }
  };

  const clear = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setIsConfirmed(false);
    onSave(null);
  };

  return (
    <div className={`signature-pad-container${isMobileViewport && isPadMaximized ? " signature-pad-container--maximized" : ""}`}>
      {isMobileViewport && (
        <p className="signature-mobile-hint">Rotate your device for easier signing.</p>
      )}
      <div className={`signature-pad-wrapper${isMobileViewport ? " signature-pad-wrapper--mobile" : ""}${isMobileViewport && isPadMaximized ? " signature-pad-wrapper--expanded" : ""}`}>
        <canvas
          ref={canvasRef}
          className="signature-canvas"
          style={{ opacity: isConfirmed ? 0.7 : 1, cursor: (isConfirmed || disabled) ? 'not-allowed' : 'crosshair' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="signature-placeholder" style={{ display: hasSignature ? 'none' : 'block' }}>Sign here</div>
      </div>
      {!disabled && (
        <div className="signature-actions">
          {isMobileViewport && (
            <button
              type="button"
              onClick={() => setIsPadMaximized((prev) => !prev)}
              className="button-outline-sm signature-action-icon-btn"
              aria-label={isPadMaximized ? "Minimize signature pad" : "Maximize signature pad"}
              title={isPadMaximized ? "Minimize" : "Maximize"}
            >
              {isPadMaximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          )}
          <button
            type="button"
            onClick={clear}
            className={`button-outline-sm${isMobileViewport ? " signature-action-icon-btn" : ""}`}
            aria-label="Clear signature"
            title="Clear"
          >
            <RotateCcw size={14} />
            {!isMobileViewport && " Clear"}
          </button>
          {!isConfirmed && (
            <button 
              type="button" 
              onClick={handleConfirm} 
              disabled={!hasSignature} 
              className="button-primary-sm"
              style={{ marginLeft: '10px' }}
            >
              <CheckCircle2 size={14} style={{ marginRight: '5px' }} /> Confirm
            </button>
          )}
          {isConfirmed && (
            <span style={{ color: 'green', marginLeft: '10px', display: 'flex', alignItems: 'center' }}>
              <CheckCircle2 size={14} style={{ marginRight: '5px' }} /> Confirmed
            </span>
          )}
        </div>
      )}
    </div>
  );
}


const maritalStatusOptions = [
  "Single",
  "Married",
  "Widowed",
  "Separated",
  "Live-in partner",
];

const genderOptions = [
  "Male",
  "Female",
  "Other",
  "Prefer not to say",
];

const educationOptions = [
  "Elementary Graduate",
  "High School Graduate",
  "College Undergraduate",
  "College Graduate",
  "Vocational",
  "Master's Degree",
  "Doctorate Degree",
];

const religionOptions = [
  "Roman Catholic",
  "Islam",
  "Iglesia ni Cristo",
  "Seventh Day Adventist",
  "Bible Baptist Church",
  "Jehovah's Witnesses",
  "Born Again Christian",
  "Others",
];

const unionOptions = [
  "Luzon Workers Union (LWU)",
  "Trade Union Congress of the Philippines (TUCP)",
  "Federation of Free Workers (FFW)",
  "Kilusang Mayo Uno (KMU)",
  "Sentro ng mga Nagkakaisa at Progresibong Manggagawa (Sentro)",
  "Others",
];

const unionAffiliationOptions = [
  "LWU",
  "PPEU",
];

const unionPositionOptions = [
  "Member",
  "Shop Steward",
  "Board Member",
  "Auditor",
  "Treasurer",
  "Secretary",
  "Vice President",
  "President",
];

const relationshipOptions = [
  "Spouse",
  "Parent",
  "Child",
  "Sibling",
  "Friend",
  "Other",
];

const toIsoDate = ({ month, day, year }) => {
  if (!month || !day || !year) return "";
  const mm = Number(month);
  const dd = Number(day);
  const yyyy = Number(year);
  if (!Number.isFinite(mm) || !Number.isFinite(dd) || !Number.isFinite(yyyy)) return "";
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || year.length !== 4) return "";
  const date = new Date(yyyy, mm - 1, dd);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const computeAge = ({ month, day, year }) => {
  if (!month || !day || !year) return "";
  const mm = Number(month);
  const dd = Number(day);
  const yyyy = Number(year);
  if (!Number.isFinite(mm) || !Number.isFinite(dd) || !Number.isFinite(yyyy)) return "";
  const dob = new Date(yyyy, mm - 1, dd);
  if (Number.isNaN(dob.getTime())) return "";
  const diff = Date.now() - dob.getTime();
  if (diff < 0) return "";
  const ageDate = new Date(diff);
  return Math.abs(ageDate.getUTCFullYear() - 1970).toString();
};

const requiredString = (value) => {
  if (value === null || value === undefined) return false;
  return String(value).trim().length > 0;
};

const displayValue = (value) => {
  if (value === null || value === undefined) return "—";
  const text = String(value).trim();
  return text.length ? text : "—";
};

const formatYearsEmployed = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return value;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;

  const amount = Number.isInteger(numeric)
    ? String(numeric)
    : String(parseFloat(numeric.toFixed(2)));
  const unit = Math.abs(numeric) === 1 ? "year" : "years";
  return `${amount} ${unit}`;
};

const hasValidSignature = (value) => {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  if (!text) return false;
  const lowered = text.toLowerCase();
  return lowered !== 'null' && lowered !== 'undefined';
};

const buildEmergencyAddressString = (contact = {}) => {
  const parts = [
    contact.address1,
    contact.barangay,
    contact.city,
    contact.province,
    contact.postalCode,
  ].filter(Boolean);

  return parts.join(", ");
};

const mirrorEmergencyAddressFromPersonal = (formState) => {
  const mirroredEmergency = {
    ...formState.emergencyContact,
    region: formState.region,
    province: formState.province,
    city: formState.city,
    barangay: formState.barangay,
    address1: formState.address1,
    postalCode: formState.postalCode,
  };

  mirroredEmergency.address = buildEmergencyAddressString(mirroredEmergency);

  return {
    ...formState,
    emergencyContact: mirroredEmergency,
  };
};

const initialFormState = {
  firstName: "",
  middleInitial: "",
  lastName: "",
  placeOfBirth: "",
  region: "",
  province: "",
  city: "",
  barangay: "",
  address1: "",
  // address2 is removed/unused in new flow but keeping for compatibility if needed, though we will use barangay dropdown
  postalCode: "",
  maritalStatus: "",
  numberOfChildren: "",
  gender: "",
  otherGender: "",
  religion: "",
  otherReligion: "",
  education: "",
  email: "",
  phone: "",
  company: "",
  position: "",
  department: "",
  yearsEmployed: "",
  unionAffiliation: "",
  otherUnionAffiliation: "",
  unionPosition: "",
  emergencyContact: {
    name: "",
    relationship: "",
    phone: "",
    address: "",
    region: "",
    province: "",
    city: "",
    barangay: "",
    address1: "",
    // address2 (old) mapped to barangay now for consistency if needed, but we separate them
    postalCode: "",
  },
  dateOfBirth: {
    month: "",
    day: "",
    year: "",
  },
};

export default function RegistrationPage({ onSubmit, onBack, submitting, submitError, initialData, readOnly, initialStep }) {
  const [form, setForm] = useState({ ...initialFormState, ...initialData });
  const [profilePreview, setProfilePreview] = useState(initialData?.profilePicture || null);
  const [photoCropSource, setPhotoCropSource] = useState(null);
  const [photoCrop, setPhotoCrop] = useState({ x: 0, y: 0 });
  const photoCropperStageRef = useRef(null);
  const [photoCropSize, setPhotoCropSize] = useState(undefined);
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoMinZoom, setPhotoMinZoom] = useState(1);
  const [photoCroppedAreaPixels, setPhotoCroppedAreaPixels] = useState(null);
  const [photoCropError, setPhotoCropError] = useState("");
  const [isApplyingPhotoCrop, setIsApplyingPhotoCrop] = useState(false);
  const [isValidatingPhotoHuman, setIsValidatingPhotoHuman] = useState(false);
  const [isPhotoCropInteracting, setIsPhotoCropInteracting] = useState(false);
  const [errors, setErrors] = useState({});
  const [useSameAddress, setUseSameAddress] = useState(false);
  const [currentStep, setCurrentStep] = useState(() => {
    const parsed = Number(initialStep);
    if (!Number.isFinite(parsed)) return 1;
    return Math.min(6, Math.max(1, Math.trunc(parsed)));
  });
  const [signature, setSignature] = useState(initialData?.signature || null);
  const [signaturePreviewIsPortrait, setSignaturePreviewIsPortrait] = useState(false);
  const [privacyConfirmed, setPrivacyConfirmed] = useState(false);
  const [showBackWarning, setShowBackWarning] = useState(false);
  const photoMaxZoom = Math.max(3, Number((photoMinZoom + 2).toFixed(2)));

  // Dynamic dropdown data from backend
  const [dropdownData, setDropdownData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchDropdown = async () => {
      try {
        const resp = await fetch(`${BASE_URL}/api/union-data/dropdown`);
        if (resp.ok) {
          const data = await resp.json();
          if (!cancelled) setDropdownData(data);
        }
      } catch (_err) {
        // Fallback to hardcoded data silently
      }
    };
    fetchDropdown();
    return () => { cancelled = true; };
  }, []);

  // Build dynamic options from API data or fallback to hardcoded
  const dynamicAffiliationOptions = useMemo(() => {
    if (dropdownData?.affiliations?.length) {
      return dropdownData.affiliations.map((a) => a.acronym);
    }
    return Object.keys(unionAffiliationData);
  }, [dropdownData]);

  const dynamicCompanyOptions = useMemo(() => {
    if (!form.unionAffiliation) return [];
    if (dropdownData?.companies?.length) {
      return dropdownData.companies
        .filter((c) => c.affiliationAcronym === form.unionAffiliation)
        .map((c) => c.name);
    }
    if (unionAffiliationData[form.unionAffiliation]) {
      return Object.keys(unionAffiliationData[form.unionAffiliation]);
    }
    return [];
  }, [dropdownData, form.unionAffiliation]);

  const dynamicUnionNameOptions = useMemo(() => {
    if (!form.unionAffiliation || !form.company) return [];
    if (dropdownData?.unionNames?.length && dropdownData?.companies?.length) {
      const companyEntry = dropdownData.companies.find(
        (c) => c.name === form.company && c.affiliationAcronym === form.unionAffiliation
      );
      if (companyEntry) {
        return dropdownData.unionNames
          .filter((u) => String(u.companyId) === String(companyEntry.id))
          .map((u) => u.formattedName);
      }
    }
    if (unionAffiliationData[form.unionAffiliation]?.[form.company]) {
      return unionAffiliationData[form.unionAffiliation][form.company];
    }
    return [];
  }, [dropdownData, form.unionAffiliation, form.company]);

  const dynamicPositionOptions = useMemo(() => {
    if (dropdownData?.positions?.length) {
      return dropdownData.positions.map((p) => p.name);
    }
    return unionPositionOptions;
  }, [dropdownData]);

  // Update form if initialData changes
  useEffect(() => {
    if (initialData) {
      // Auto-detect region if province exists but region doesn't
      let inferredRegion = initialData.region || "";
      if (!inferredRegion && initialData.province) {
         for (const [reg, provinces] of Object.entries(ADDRESS_DATA)) {
             if (Object.keys(provinces).includes(initialData.province)) {
                 inferredRegion = reg;
                 break;
             }
         }
      }

      setForm((prev) => ({
         ...prev, 
         ...initialData,
        numberOfChildren: initialData?.numberOfChildren ?? prev.numberOfChildren ?? "",
        yearsEmployed: initialData?.yearsEmployed ?? prev.yearsEmployed ?? "",
         region: inferredRegion,
         // Map address2 to barangay if barangay is missing but address2 is present
         barangay: initialData.barangay || initialData.address2 || "", 
      }));
      if (initialData.profilePicture) {
        setProfilePreview(initialData.profilePicture);
      }
      setPhotoCropSource(null);
      setPhotoCrop({ x: 0, y: 0 });
      setPhotoZoom(1);
      setPhotoMinZoom(1);
      setPhotoCroppedAreaPixels(null);
      setPhotoCropError("");
      setIsValidatingPhotoHuman(false);
      setIsPhotoCropInteracting(false);
      if (initialData.signature) {
        setSignature(initialData.signature);
      }
      setPrivacyConfirmed(false);
    }
  }, [initialData]);

  useEffect(() => {
    const parsed = Number(initialStep);
    if (!Number.isFinite(parsed)) return;
    const normalizedStep = Math.min(6, Math.max(1, Math.trunc(parsed)));
    setCurrentStep(normalizedStep);
  }, [initialStep]);

  useEffect(() => {
    setSignaturePreviewIsPortrait(false);
  }, [signature]);

  useEffect(() => {
    if (!photoCropSource) {
      setPhotoCropSize(undefined);
      return undefined;
    }

    const stageElement = photoCropperStageRef.current;
    if (!stageElement) return undefined;

    const syncCropSize = () => {
      const width = Math.floor(stageElement.clientWidth);
      const height = Math.floor(stageElement.clientHeight);
      if (width > 0 && height > 0) {
        const size = Math.min(width, height);
        setPhotoCropSize({ width: size, height: size });
      }
    };

    syncCropSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", syncCropSize);
      return () => window.removeEventListener("resize", syncCropSize);
    }

    const resizeObserver = new ResizeObserver(syncCropSize);
    resizeObserver.observe(stageElement);

    return () => resizeObserver.disconnect();
  }, [photoCropSource]);

  useEffect(() => {
    if (!isPhotoCropInteracting) return undefined;

    const stopInteraction = () => setIsPhotoCropInteracting(false);
    window.addEventListener("pointerup", stopInteraction);
    window.addEventListener("pointercancel", stopInteraction);

    return () => {
      window.removeEventListener("pointerup", stopInteraction);
      window.removeEventListener("pointercancel", stopInteraction);
    };
  }, [isPhotoCropInteracting]);

  const ageValue = useMemo(() => computeAge(form.dateOfBirth), [form.dateOfBirth]);
  
  const handlePhotoUpload = async (e) => {
    if (readOnly) return;
    const file = e.target.files[0];
    if (file) {
      try {
        const imageSource = await fileToDataUrl(file);
        setPhotoCropSource(imageSource);
        setPhotoCrop({ x: 0, y: 0 });
        setPhotoZoom(1);
        setPhotoMinZoom(1);
        setPhotoCroppedAreaPixels(null);
        setPhotoCropError("");
        setIsPhotoCropInteracting(false);
      } catch (error) {
        setPhotoCropError("Unable to load the selected photo. Please try another file.");
      }
    }

    e.target.value = "";
  };

  const handlePhotoCropComplete = (_, croppedAreaPixels) => {
    setPhotoCroppedAreaPixels(croppedAreaPixels);
  };

  const handlePhotoMediaLoaded = (mediaSize) => {
    const mediaWidth = mediaSize?.naturalWidth ?? mediaSize?.width;
    const mediaHeight = mediaSize?.naturalHeight ?? mediaSize?.height;
    const nextMinZoom = computeMinimumCropZoom(mediaWidth, mediaHeight);

    setPhotoMinZoom(nextMinZoom);
    setPhotoZoom((currentZoom) => Math.max(currentZoom, nextMinZoom));
  };

  const validateHumanPhoto = async (photoDataUrl) => {
    const validationUserId = initialData?.id || form?.id;
    if (!validationUserId) {
      throw new Error("Unable to validate photo at this time. Please refresh and try again.");
    }

    if (!photoDataUrl || typeof photoDataUrl !== "string" || !photoDataUrl.startsWith("data:image")) {
      throw new Error("Please upload and crop a valid photo first.");
    }

    setIsValidatingPhotoHuman(true);
    const validateResponse = await fetch(`${BASE_URL}/api/users/${validationUserId}/validate-avatar-human`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ profilePicture: photoDataUrl }),
    });

    const validateData = await validateResponse.json().catch(() => ({}));
    if (!validateResponse.ok) {
      throw new Error(validateData.error || validateData.message || "Unable to validate profile photo.");
    }

    if (!validateData.human_detected) {
      throw new Error("Please upload a photo that clearly shows a human face.");
    }
  };

  const applyPhotoCrop = async () => {
    if (!photoCropSource || !photoCroppedAreaPixels) {
      setPhotoCropError("Adjust and confirm the crop before continuing.");
      return;
    }

    try {
      setIsApplyingPhotoCrop(true);
      const croppedPhotoDataUrl = await getCroppedImageDataUrl(
        photoCropSource,
        photoCroppedAreaPixels,
        600,
        "image/jpeg",
        0.92
      );

      await validateHumanPhoto(croppedPhotoDataUrl);

      setProfilePreview(croppedPhotoDataUrl);
      setForm((prev) => ({ ...prev, profilePicture: croppedPhotoDataUrl }));
      setPhotoCropSource(null);
      setPhotoMinZoom(1);
      setPhotoCropError("");
      setIsPhotoCropInteracting(false);
      setErrors((prev) => ({ ...prev, profilePicture: null }));
    } catch (error) {
      setPhotoCropError(error?.message || "Unable to apply crop. Please try again.");
    } finally {
      setIsApplyingPhotoCrop(false);
      setIsValidatingPhotoHuman(false);
    }
  };

  // Check for errors without setting state
  const checkErrors = (step) => {
    const newErrors = {};
    const dobIso = toIsoDate(form.dateOfBirth);
    const contact = form.emergencyContact;

    if (step === 1) {
        if (!requiredString(form.firstName)) newErrors.firstName = "First Name is required";
        if (!requiredString(form.lastName)) newErrors.lastName = "Last Name is required";
        if (!dobIso) newErrors.dateOfBirth = "Valid Date of Birth is required";
        if (!requiredString(form.placeOfBirth)) newErrors.placeOfBirth = "Place of Birth is required";
      if (!requiredString(form.region)) newErrors.region = "Region is required";
        if (!requiredString(form.address1)) newErrors.address1 = "Address Line 1 is required";
        if (!requiredString(form.city)) newErrors.city = "City is required";
      if (!requiredString(form.barangay)) newErrors.barangay = "Barangay is required";
        if (!requiredString(form.province)) newErrors.province = "Province is required";
        if (!requiredString(form.postalCode)) newErrors.postalCode = "Postal Code is required";
        if (!requiredString(form.education)) newErrors.education = "Education is required";
        if (!requiredString(form.email)) newErrors.email = "Email is required";
        if (!requiredString(form.numberOfChildren)) newErrors.numberOfChildren = "No. of Children is required";
        
        const phoneDigits = form.phone?.replace(/\D/g, '') || '';
        if (!phoneDigits) {
            newErrors.phone = "Phone number is required";
        } else if (!phoneDigits.startsWith('09')) {
            newErrors.phone = "Phone number must start with 09";
        } else if (phoneDigits.length !== 11) {
            newErrors.phone = "Phone number must be 11 digits";
        }
        
        if (!form.maritalStatus) newErrors.maritalStatus = "Marital Status is required";
        if (!form.gender) newErrors.gender = "Gender is required";
        if (form.gender === "Other" && !requiredString(form.otherGender)) newErrors.otherGender = "Please specify your gender";
        if (!form.religion) newErrors.religion = "Religion is required";
        if (form.religion === "Others" && !requiredString(form.otherReligion)) newErrors.otherReligion = "Please specify your religion";
    }

    if (step === 2) {
        if (!requiredString(form.company)) newErrors.company = "Company is required";
        if (!requiredString(form.position)) newErrors.position = "Position is required";
        if (!requiredString(form.yearsEmployed)) newErrors.yearsEmployed = "Years Employed is required";
        if (!requiredString(form.unionPosition)) newErrors.unionPosition = "Union Position is required";
        if (form.unionAffiliation === "Others" && !requiredString(form.otherUnionAffiliation)) newErrors.otherUnionAffiliation = "Please specify your union affiliation";
    }

    if (step === 3) {
        if (!requiredString(contact.name)) newErrors['emergencyContact.name'] = "Emergency Contact Name is required";
        if (!requiredString(contact.relationship)) newErrors['emergencyContact.relationship'] = "Emergency Contact Relationship is required";
      if (!requiredString(contact.region)) newErrors['emergencyContact.region'] = "Emergency Contact Region is required";
      if (!requiredString(contact.province)) newErrors['emergencyContact.province'] = "Emergency Contact Province is required";
      if (!requiredString(contact.city)) newErrors['emergencyContact.city'] = "Emergency Contact City/Municipality is required";
      if (!requiredString(contact.barangay)) newErrors['emergencyContact.barangay'] = "Emergency Contact Barangay is required";
      if (!requiredString(contact.postalCode)) newErrors['emergencyContact.postalCode'] = "Emergency Contact Postal Code is required";
      if (!requiredString(contact.address1)) newErrors['emergencyContact.address1'] = "Emergency Contact Address Line 1 is required";
        
        const ecPhoneDigits = contact.phone?.replace(/\D/g, '') || '';
        if (!ecPhoneDigits) {
            newErrors['emergencyContact.phone'] = "Emergency Contact Phone is required";
        } else if (!ecPhoneDigits.startsWith('09')) {
            newErrors['emergencyContact.phone'] = "Phone number must start with 09";
        } else if (ecPhoneDigits.length !== 11) {
            newErrors['emergencyContact.phone'] = "Phone number must be 11 digits";
        }
        
        if (!requiredString(contact.address)) newErrors['emergencyContact.address'] = "Emergency Contact Address is required";
    }
    
    if (step === 4) {
        if (!profilePreview) newErrors.profilePicture = "Profile Picture is required";
    }

    if (step === 5) {
      if (!hasValidSignature(signature)) newErrors.signature = "Signature confirmation is required";
    }

    if (step === 6 && !readOnly) {
      if (!privacyConfirmed) {
        newErrors.privacyConsent = "Please confirm the Data Privacy consent before submitting.";
      }
    }
    
    return newErrors;
  };
  
  // Memoize validity to prevent recalculations on every render
  const isStepValid = useMemo(() => {
      if (readOnly) return true;
      const stepErrors = checkErrors(currentStep);
      return Object.keys(stepErrors).length === 0;
    }, [form, currentStep, profilePreview, signature, readOnly, privacyConfirmed]);

  const handleNext = (e) => {
      // Prevent default form submission if triggered by button inside form
      if (e) e.preventDefault();

      if (currentStep === 4 && photoCropSource) {
        setPhotoCropError("Please apply the crop before continuing.");
        return;
      }

      if (readOnly) {
        setErrors({});
        window.scrollTo(0, 0);
        setCurrentStep(prev => prev + 1);
        return;
      }
      
      const stepErrors = checkErrors(currentStep);
      if (Object.keys(stepErrors).length === 0) {
          setErrors({});
          window.scrollTo(0, 0);
          setCurrentStep(prev => prev + 1);
      } else {
          setErrors(stepErrors);
      }
  };


  const handleBackStep = () => {
      window.scrollTo(0, 0);
      setCurrentStep(prev => prev - 1);
  };

  const handleFinalSubmit = (e) => {
      e.preventDefault();

      if (readOnly) return;

      const submitErrors = checkErrors(6);
      if (Object.keys(submitErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...submitErrors }));
        return;
      }

      const dobIso = toIsoDate(form.dateOfBirth);
      // Validations should have been done by steps, but safety check for DOB
      if (!dobIso) return; 

      const payload = {
        firstName: form.firstName.trim(),
        middleInitial: form.middleInitial.trim() || null,
        lastName: form.lastName.trim(),
        placeOfBirth: form.placeOfBirth.trim(),
        region: form.region ? form.region.trim() : null,
        email: form.email.trim(),
        phone: form.phone.trim(),
        address_line1: form.address1.trim(),
        // Map barangay to address_line2
        address_line2: form.barangay ? form.barangay.trim() : null,
        barangay: form.barangay ? form.barangay.trim() : null,
        city: form.city.trim(),
        province: form.province.trim(),
        postal_code: form.postalCode.trim(),
        dateOfBirth: dobIso,
        maritalStatus: form.maritalStatus,
        numberOfChildren: form.numberOfChildren === "" ? null : Number(form.numberOfChildren),
        gender: form.gender,
        otherGender: form.gender === "Other" ? form.otherGender.trim() : null,
        religion: form.religion === "Others" ? form.otherReligion.trim() : form.religion,
        education: form.education.trim(),
        company: form.company.trim(),
        position: form.position.trim(),
        department: form.department ? form.department.trim() : null,
        yearsEmployed: form.yearsEmployed === "" ? null : Number(form.yearsEmployed),
        unionAffiliation: form.unionAffiliation === "Others" ? form.otherUnionAffiliation.trim() : form.unionAffiliation,
        unionPosition: form.unionPosition.trim(),
        emergencyContact: {
            name: form.emergencyContact.name.trim(),
            relationship: form.emergencyContact.relationship.trim(),
            phone: form.emergencyContact.phone.trim(),
            address: form.emergencyContact.address.trim(),
          region: form.emergencyContact.region ? form.emergencyContact.region.trim() : null,
          province: form.emergencyContact.province ? form.emergencyContact.province.trim() : null,
          city: form.emergencyContact.city ? form.emergencyContact.city.trim() : null,
          barangay: form.emergencyContact.barangay ? form.emergencyContact.barangay.trim() : null,
          postalCode: form.emergencyContact.postalCode ? form.emergencyContact.postalCode.trim() : null,
          addressLine1: form.emergencyContact.address1 ? form.emergencyContact.address1.trim() : null,
        },
        profilePicture: profilePreview,
        signature: signature
      };
      
      onSubmit(payload);
  };


  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Limit to 11 digits
    const truncated = digits.slice(0, 11);
    
    // Format: 09XX XXX XXXX
    if (truncated.length > 7) {
      return `${truncated.slice(0, 4)} ${truncated.slice(4, 7)} ${truncated.slice(7)}`;
    } else if (truncated.length > 4) {
      return `${truncated.slice(0, 4)} ${truncated.slice(4)}`;
    }
    return truncated;
  };

  const handleInputChange = (event) => {
    if (readOnly) return;
    const { name, value } = event.target;
    
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setForm((prev) => ({ ...prev, [name]: formatted }));
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
      }
      return;
    }

    if (name === 'unionAffiliation') {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        company: '', // Reset dependent company
        department: '' // Reset dependent union name
      }));
      setErrors(prev => ({ ...prev, unionAffiliation: null, company: null, department: null }));
      return;
    }

    if (name === 'company') {
        setForm((prev) => ({
            ...prev,
            [name]: value,
            department: '' // Reset dependent union name
        }));
        setErrors(prev => ({ ...prev, company: null, department: null }));
        return;
    }

    setForm((prev) => {
      const newForm = { ...prev, [name]: value };
      if (useSameAddress && ['address1', 'postalCode'].includes(name)) {
        return mirrorEmergencyAddressFromPersonal(newForm);
      }
      return newForm;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleAddressChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setForm((prev) => {
      let updates = { [name]: value };
      
      if (name === "region") {
        updates.province = "";
        updates.city = "";
        updates.barangay = "";
      } else if (name === "province") {
        updates.city = "";
        updates.barangay = "";
      } else if (name === "city") {
        updates.barangay = "";
      }
      
      const newForm = { ...prev, ...updates };

      if (useSameAddress) {
        return mirrorEmergencyAddressFromPersonal(newForm);
      }

      return newForm;
    });
    
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleEmergencyChange = (event) => {
    if (readOnly) return;
    const { name, value } = event.target;
    
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setForm((prev) => ({
        ...prev,
        emergencyContact: { ...prev.emergencyContact, [name]: formatted },
      }));
      if (errors[`emergencyContact.${name}`]) {
        setErrors(prev => ({ ...prev, [`emergencyContact.${name}`]: null }));
      }
      return;
    }

    if (['address1', 'postalCode'].includes(name)) {
      setForm((prev) => {
        const newEc = { ...prev.emergencyContact, [name]: value };
        const parts = [
          newEc.address1, newEc.barangay, newEc.city, newEc.province, newEc.postalCode
        ].filter(Boolean);
        newEc.address = parts.join(", ");
        return { ...prev, emergencyContact: newEc };
      });
      return;
    }

    if (['region', 'province', 'city', 'barangay'].includes(name)) {
         setForm((prev) => {
            let updates = { [name]: value };
            const currentEc = prev.emergencyContact;

            if (name === "region") {
                updates.province = "";
                updates.city = "";
                updates.barangay = "";
            } else if (name === "province") {
                updates.city = "";
                updates.barangay = "";
            } else if (name === "city") {
                updates.barangay = "";
            }
            
            const newEc = { ...currentEc, ...updates };
             
            // Rebuild full address string
            const parts = [
                newEc.address1,
                newEc.barangay,
                newEc.city,
                newEc.province,
                newEc.postalCode
            ].filter(Boolean);
            newEc.address = parts.join(", ");

            return { ...prev, emergencyContact: newEc };
         });
         return;
    }

    setForm((prev) => ({
      ...prev,
      emergencyContact: { ...prev.emergencyContact, [name]: value },
    }));
    if (errors[`emergencyContact.${name}`]) {
      setErrors(prev => ({ ...prev, [`emergencyContact.${name}`]: null }));
    }
  };

  const toggleSameAddress = (nextValue) => {
    if (readOnly) return;
    const isChecked = Boolean(nextValue);
    setUseSameAddress(isChecked);
    
    if (isChecked) {
      setForm(current => {
        return mirrorEmergencyAddressFromPersonal(current);
      });
    } else {
      setForm(current => {
        const newEc = {
          ...current.emergencyContact,
          region: "", province: "", city: "", barangay: "", address1: "", postalCode: "", address: ""
        };
        return { ...current, emergencyContact: newEc };
      });
    }
  };

  const handleDateChange = (e) => {
    if (readOnly) return;
    const dateString = e.target.value;
    if (!dateString) {
       setForm(prev => ({ ...prev, dateOfBirth: { month: "", day: "", year: "" } }));
       return;
    }
    const [year, month, day] = dateString.split("-");
    setForm(prev => ({ ...prev, dateOfBirth: { year, month, day } }));
    if (errors.dateOfBirth) setErrors(prev => ({ ...prev, dateOfBirth: null }));
  };

  // Steps definition for the wizard
  const steps = [
    { id: 1, label: "Personal" },
    { id: 2, label: "Employment" },
    { id: 3, label: "Contact" },
    { id: 4, label: "Photo" },
    { id: 5, label: "Signature" },
    { id: 6, label: "Confirm" },
  ];

  const renderReviewRow = (label, value) => (
    <div className="review-data-row">
      <strong>{label}</strong>
      <span>:</span>
      <span>{displayValue(value)}</span>
    </div>
  );

  return (
    <div className="membership-form">
      <div className="membership-form__container">
        <header className="membership-form__header">
          <button type="button" className="membership-form__back" onClick={() => {
            // If they are not in read-only mode, and they might have filled something, show warning.
            if (!readOnly) {
              setShowBackWarning(true);
            } else {
              onBack();
            }
          }}>
            <ArrowLeft size={16} /> Back
          </button>
          <div className="membership-form__heading">
            <h1>Membership Registration</h1>
            <p>Step {currentStep} of {steps.length}: {steps[currentStep - 1].label}</p>
          </div>
        </header>
        
        <div className="membership-form__card" style={{ padding: '0 0 24px' }}>
            {/* Progress Bar */}
            <div className="wizard-progress" style={{ padding: '32px 36px 16px' }}>
                <div className="wizard-progress__bar-container" style={{ height: '6px', background: '#f1f5f9', borderRadius: '3px', marginBottom: '24px', overflow: 'hidden' }}>
                    <div 
                        className="wizard-progress__bar" 
                        style={{ height: '100%', background: '#2563eb', transition: 'width 0.3s ease', width: `${(currentStep / steps.length) * 100}%` }}
                    />
                </div>
                <div className="wizard-progress__steps" style={{ display: 'flex', justifyContent: 'space-between' }}>
                {steps.map((step) => {
                  const stepNode = (
                    <>
                      <div className="wizard-step__circle" style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: currentStep > step.id ? '#22c55e' : (currentStep === step.id ? '#2563eb' : '#f1f5f9'),
                          color: currentStep >= step.id ? '#fff' : '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.3s ease'
                      }}>
                          {currentStep > step.id ? <CheckCircle2 size={16} /> : step.id}
                      </div>
                      <span className="wizard-step__label">{step.label}</span>
                    </>
                  );

                  if (readOnly) {
                    return (
                      <button
                        type="button"
                        key={step.id}
                        className={`wizard-step ${currentStep >= step.id ? 'active' : ''} ${currentStep === step.id ? 'current' : ''}`}
                        onClick={() => {
                          setCurrentStep(step.id);
                          window.scrollTo(0, 0);
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                          color: currentStep >= step.id ? '#0f172a' : '#94a3b8',
                          fontWeight: currentStep === step.id ? '600' : '500',
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer'
                        }}
                      >
                        {stepNode}
                      </button>
                    );
                  }

                  return (
                    <div
                      key={step.id}
                      className={`wizard-step ${currentStep >= step.id ? 'active' : ''} ${currentStep === step.id ? 'current' : ''}`}
                      style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                          color: currentStep >= step.id ? '#0f172a' : '#94a3b8',
                          fontWeight: currentStep === step.id ? '600' : '500'
                      }}
                    >
                      {stepNode}
                    </div>
                  );
                })}
                </div>
            </div>

            <div className="wizard-content" style={{ padding: '0 36px' }}>
                {submitError && (
                <div className="membership-form__alert membership-form__alert--error">
                    <AlertCircle size={18} />
                    <span>{submitError}</span>
                </div>
                )}

                <form onSubmit={(e) => {
                    e.preventDefault();
                  if (!readOnly && currentStep === steps.length) {
                        handleFinalSubmit(e);
                    }
                }} noValidate>
              <fieldset disabled={readOnly} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          {/* Step 1: Personal Information */}
          <FormSection title="Personal Information" isActive={currentStep === 1}>
            <div className="membership-form__fields membership-form__fields--cols-3">
              <label className={`membership-form__field ${errors.firstName ? "membership-form__field--error" : ""}`}>
                <span>First Name *</span>
                <input name="firstName" value={form.firstName} onChange={handleInputChange} required />
                {errors.firstName && <span className="membership-form__error-message">{errors.firstName}</span>}
              </label>
              <label className="membership-form__field">
                <span>Middle Name</span>
                <input name="middleInitial" value={form.middleInitial} onChange={handleInputChange} />
              </label>
              <label className={`membership-form__field ${errors.lastName ? "membership-form__field--error" : ""}`}>
                <span>Last Name *</span>
                <input name="lastName" value={form.lastName} onChange={handleInputChange} required />
                {errors.lastName && <span className="membership-form__error-message">{errors.lastName}</span>}
              </label>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-3">
              <label className={`membership-form__field ${errors.dateOfBirth ? "membership-form__field--error" : ""}`}>
                <span>Date of Birth *</span>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={form.dateOfBirth.year && form.dateOfBirth.month && form.dateOfBirth.day ? `${form.dateOfBirth.year}-${form.dateOfBirth.month.toString().padStart(2, '0')}-${form.dateOfBirth.day.toString().padStart(2, '0')}` : ''}
                  onChange={handleDateChange}
                  required
                  max={new Date().toISOString().split("T")[0]}
                />
                {errors.dateOfBirth && <span className="membership-form__error-message">{errors.dateOfBirth}</span>}
              </label>
              <label className="membership-form__field">
                <span>Age</span>
                <input value={ageValue} readOnly placeholder="--" />
              </label>
              <label className={`membership-form__field ${errors.placeOfBirth ? "membership-form__field--error" : ""}`}>
                <span>Place of Birth *</span>
                <input name="placeOfBirth" value={form.placeOfBirth} onChange={handleInputChange} required />
                {errors.placeOfBirth && <span className="membership-form__error-message">{errors.placeOfBirth}</span>}
              </label>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-2">
              <label className={`membership-form__field ${errors.gender ? "membership-form__field--error" : ""}`}>
                <span>Gender *</span>
                <select name="gender" value={form.gender} onChange={handleInputChange} required>
                  <option value="" disabled>Select Gender</option>
                  {genderOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {errors.gender && <span className="membership-form__error-message">{errors.gender}</span>}
                {form.gender === "Other" && (
                  <input
                    type="text"
                    name="otherGender"
                    placeholder="Please specify gender"
                    value={form.otherGender}
                    onChange={handleInputChange}
                    style={{ marginTop: '0.5rem' }}
                    className={errors.otherGender ? "membership-form__input--error" : ""}
                  />
                )}
              </label>
              <label className={`membership-form__field ${errors.maritalStatus ? "membership-form__field--error" : ""}`}>
                <span>Civil Status *</span>
                <select name="maritalStatus" value={form.maritalStatus} onChange={handleInputChange} required>
                  <option value="" disabled>Select Status</option>
                  {maritalStatusOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {errors.maritalStatus && <span className="membership-form__error-message">{errors.maritalStatus}</span>}
              </label>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-3">
               <label className={`membership-form__field ${errors.numberOfChildren ? "membership-form__field--error" : ""}`}>
                <span>No. of Children *</span>
                <input name="numberOfChildren" type="number" min="0" value={form.numberOfChildren} onChange={handleInputChange} required />
                {errors.numberOfChildren && <span className="membership-form__error-message">{errors.numberOfChildren}</span>}
              </label>
              <label className={`membership-form__field ${errors.religion ? "membership-form__field--error" : ""}`}>
                <span>Religion</span>
                <select name="religion" value={form.religion} onChange={handleInputChange}>
                  <option value="" disabled>Select Religion</option>
                  {religionOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {form.religion === "Others" && (
                  <input
                    name="otherReligion"
                    placeholder="Please specify"
                    value={form.otherReligion}
                    onChange={handleInputChange}
                    className={`mt-2 ${errors.otherReligion ? "membership-form__input--error" : ""}`}
                  />
                )}
              </label>
              <label className={`membership-form__field ${errors.education ? "membership-form__field--error" : ""}`}>
                <span>Educational Attainment *</span>
                <select name="education" value={form.education} onChange={handleInputChange} required>
                  <option value="" disabled>Select Education</option>
                  {educationOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {errors.education && <span className="membership-form__error-message">{errors.education}</span>}
              </label>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-2">
                <label className={`membership-form__field ${errors.region ? "membership-form__field--error" : ""}`}>
                  <span>Region *</span>
                  <select name="region" value={form.region} onChange={handleAddressChange} required>
                    <option value="">Select Region</option>
                    {Object.keys(ADDRESS_DATA).map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  {errors.region && <span className="membership-form__error-message">{errors.region}</span>}
                </label>
                <label className={`membership-form__field ${errors.province ? "membership-form__field--error" : ""}`}>
                  <span>Province *</span>
                  <select
                    name="province"
                    value={form.province}
                    onChange={handleAddressChange}
                    disabled={!form.region}
                    className={!form.region ? 'field-disabled' : ''}
                    required
                  >
                    <option value="">Select Province</option>
                    {form.region && ADDRESS_DATA[form.region] && Object.keys(ADDRESS_DATA[form.region]).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  {errors.province && <span className="membership-form__error-message">{errors.province}</span>}
                </label>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-2">
                <label className={`membership-form__field ${errors.city ? "membership-form__field--error" : ""}`}>
                  <span>City / Municipality *</span>
                  <select
                    name="city"
                    value={form.city}
                    onChange={handleAddressChange}
                    disabled={!form.province}
                    className={!form.province ? 'field-disabled' : ''}
                    required
                  >
                    <option value="">Select City/Municipality</option>
                    {form.region && form.province && ADDRESS_DATA[form.region][form.province] && ADDRESS_DATA[form.region][form.province].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {errors.city && <span className="membership-form__error-message">{errors.city}</span>}
                </label>
                 <label className={`membership-form__field ${errors.barangay ? "membership-form__field--error" : ""}`}>
                  <span>Barangay *</span>
                  <select
                    name="barangay"
                    value={form.barangay}
                    onChange={handleAddressChange}
                    disabled={!form.city}
                    className={!form.city ? 'field-disabled' : ''}
                    required
                  >
                    <option value="">Select Barangay</option>
                    {form.city && getBarangays(form.city).map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                  {errors.barangay && <span className="membership-form__error-message">{errors.barangay}</span>}
                </label>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-2">
              <label className={`membership-form__field ${errors.postalCode ? "membership-form__field--error" : ""}`}>
                <span>Postal Code *</span>
                <input name="postalCode" value={form.postalCode} onChange={handleInputChange} disabled={!form.barangay} className={!form.barangay ? 'field-disabled' : ''} required />
                {errors.postalCode && <span className="membership-form__error-message">{errors.postalCode}</span>}
              </label>
              <label className={`membership-form__field ${errors.address1 ? "membership-form__field--error" : ""}`}>
                <span>Address Line 1 (House No., Street) *</span>
                <input name="address1" value={form.address1} onChange={handleInputChange} placeholder="House/Unit/Block No., Street, Subdivision/Village" disabled={!form.barangay} className={!form.barangay ? 'field-disabled' : ''} required />
                {errors.address1 && <span className="membership-form__error-message">{errors.address1}</span>}
              </label>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-2">
              <label className={`membership-form__field ${errors.email ? "membership-form__field--error" : ""}`}>
                <span>Email Address *</span>
                <input name="email" type="email" value={form.email} onChange={handleInputChange} required />
                {errors.email && <span className="membership-form__error-message">{errors.email}</span>}
              </label>
              <label className={`membership-form__field ${errors.phone ? "membership-form__field--error" : ""}`}>
                <span>Contact No. *</span>
                <input name="phone" value={form.phone} onChange={handleInputChange} placeholder="09XX XXX XXXX" required maxLength={13} />
                {errors.phone && <span className="membership-form__error-message">{errors.phone}</span>}
              </label>
            </div>
          </FormSection>

          {/* Step 2: Employment Information */}
          <FormSection title="Employment Information" isActive={currentStep === 2}>
            <div className="membership-form__fields membership-form__fields--cols-3">
              <label className={`membership-form__field ${errors.unionAffiliation ? "membership-form__field--error" : ""}`}>
                <span>Union Affiliation *</span>
                <select name="unionAffiliation" value={form.unionAffiliation} onChange={handleInputChange}>
                  <option value="" disabled>Select Union Affiliation</option>
                  {dynamicAffiliationOptions.map((affiliation) => (
                    <option key={affiliation} value={affiliation}>{affiliation}</option>
                  ))}
                </select>
                {errors.unionAffiliation && <span className="membership-form__error-message">{errors.unionAffiliation}</span>}
              </label>

              <label className={`membership-form__field ${errors.company ? "membership-form__field--error" : ""}`}>
                <span>Company Name *</span>
                <select
                  name="company"
                  value={form.company}
                  onChange={handleInputChange}
                  disabled={!form.unionAffiliation}
                  style={!form.unionAffiliation ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.7 } : {}}
                >
                  <option value="" disabled>Select Company</option>
                  {dynamicCompanyOptions.map((comp) => (
                    <option key={comp} value={comp}>{comp}</option>
                  ))}
                </select>
                {errors.company && <span className="membership-form__error-message">{errors.company}</span>}
              </label>

              <label className="membership-form__field">
                <span>Union Name</span>
                <select
                  name="department"
                  value={form.department}
                  onChange={handleInputChange}
                  disabled={!form.company}
                  style={!form.company ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed', opacity: 0.7 } : {}}
                >
                    <option value="" disabled>Select Union Name</option>
                    {dynamicUnionNameOptions.map((union) => (
                      <option key={union} value={union}>{union}</option>
                    ))}
                </select>
              </label>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-3">           
              <label className={`membership-form__field ${errors.position ? "membership-form__field--error" : ""}`}>
                <span>Employment Position *</span>
                <input name="position" value={form.position} onChange={handleInputChange} required />
                {errors.position && <span className="membership-form__error-message">{errors.position}</span>}
              </label>
              
              <label className={`membership-form__field ${errors.yearsEmployed ? "membership-form__field--error" : ""}`}>
                <span>Years Employed *</span>
                <input name="yearsEmployed" type="number" min="0" value={form.yearsEmployed} onChange={handleInputChange} required />
                {errors.yearsEmployed && <span className="membership-form__error-message">{errors.yearsEmployed}</span>}
              </label>
              
              <label className={`membership-form__field ${errors.unionPosition ? "membership-form__field--error" : ""}`}>
                <span>Union Position *</span>
                <select name="unionPosition" value={form.unionPosition} onChange={handleInputChange} required>
                  <option value="" disabled>Select Position</option>
                  {dynamicPositionOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {errors.unionPosition && <span className="membership-form__error-message">{errors.unionPosition}</span>}
              </label>
            </div>
          </FormSection>

          {/* Step 3: Contact/Emergency */}
          <FormSection title="Emergency Contact" isActive={currentStep === 3}>
             <div className="membership-form__fields membership-form__fields--cols-2">
              <label className={`membership-form__field ${errors['emergencyContact.name'] ? "membership-form__field--error" : ""}`}>
                <span>Full Name *</span>
                <input name="name" value={form.emergencyContact.name} onChange={handleEmergencyChange} required />
                {errors['emergencyContact.name'] && <span className="membership-form__error-message">{errors['emergencyContact.name']}</span>}
              </label>
              <label className={`membership-form__field ${errors['emergencyContact.relationship'] ? "membership-form__field--error" : ""}`}>
                <span>Relationship *</span>
                <select name="relationship" value={form.emergencyContact.relationship} onChange={handleEmergencyChange} required>
                  <option value="" disabled>Select relationship</option>
                  {relationshipOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {errors['emergencyContact.relationship'] && <span className="membership-form__error-message">{errors['emergencyContact.relationship']}</span>}
              </label>
            </div>
            <div className="membership-form__fields membership-form__fields--cols-2">
              <label className={`membership-form__field ${errors['emergencyContact.phone'] ? "membership-form__field--error" : ""}`}>
                <span>Contact No. *</span>
                <input name="phone" value={form.emergencyContact.phone} onChange={handleEmergencyChange} placeholder="09XX XXX XXXX" required maxLength={13} />
                {errors['emergencyContact.phone'] && <span className="membership-form__error-message">{errors['emergencyContact.phone']}</span>}
              </label>
            </div>

            <div className="membership-form__section-subbanner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', marginBottom: '0.5rem' }}>
              <span>Address</span>
              <div className="address-toggle-control">
                <span className="address-toggle-control__label">Use Primary Address</span>
                <button
                  type="button"
                  className={`address-toggle${useSameAddress ? " is-active" : ""}`}
                  aria-pressed={useSameAddress}
                  aria-label="Use primary address"
                  onClick={() => toggleSameAddress(!useSameAddress)}
                />
              </div>
            </div>

            <label className={`membership-form__field ${errors['emergencyContact.region'] ? "membership-form__field--error" : ""}`}>
              <span>Region *</span>
              <select name="region" value={form.emergencyContact.region} onChange={handleEmergencyChange} disabled={useSameAddress} required>
                <option value="">Select Region</option>
                {Object.keys(ADDRESS_DATA).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {errors['emergencyContact.region'] && <span className="membership-form__error-message">{errors['emergencyContact.region']}</span>}
            </label>

            <div className="membership-form__fields membership-form__fields--cols-2">
               <label className={`membership-form__field ${errors['emergencyContact.province'] ? "membership-form__field--error" : ""}`}>
                  <span>Province *</span>
                  <select
                    name="province"
                    value={form.emergencyContact.province}
                    onChange={handleEmergencyChange}
                    disabled={useSameAddress || !form.emergencyContact.region}
                    className={!form.emergencyContact.region && !useSameAddress ? 'field-disabled' : ''}
                    required
                  >
                    <option value="">Select Province</option>
                    {form.emergencyContact.region && ADDRESS_DATA[form.emergencyContact.region] && Object.keys(ADDRESS_DATA[form.emergencyContact.region]).map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  {errors['emergencyContact.province'] && <span className="membership-form__error-message">{errors['emergencyContact.province']}</span>}
                </label>
                <label className={`membership-form__field ${errors['emergencyContact.city'] ? "membership-form__field--error" : ""}`}>
                  <span>City / Municipality *</span>
                  <select
                    name="city"
                    value={form.emergencyContact.city}
                    onChange={handleEmergencyChange}
                    disabled={useSameAddress || !form.emergencyContact.province}
                    className={!form.emergencyContact.province && !useSameAddress ? 'field-disabled' : ''}
                    required
                  >
                    <option value="">Select City/Municipality</option>
                    {form.emergencyContact.region && form.emergencyContact.province && ADDRESS_DATA[form.emergencyContact.region][form.emergencyContact.province] && ADDRESS_DATA[form.emergencyContact.region][form.emergencyContact.province].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {errors['emergencyContact.city'] && <span className="membership-form__error-message">{errors['emergencyContact.city']}</span>}
                </label>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-2">
                 <label className={`membership-form__field ${errors['emergencyContact.barangay'] ? "membership-form__field--error" : ""}`}>
                  <span>Barangay *</span>
                  <select
                    name="barangay"
                    value={form.emergencyContact.barangay}
                    onChange={handleEmergencyChange}
                    disabled={useSameAddress || !form.emergencyContact.city}
                    className={!form.emergencyContact.city && !useSameAddress ? 'field-disabled' : ''}
                    required
                  >
                    <option value="">Select Barangay</option>
                    {form.emergencyContact.city && getBarangays(form.emergencyContact.city).map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                  {errors['emergencyContact.barangay'] && <span className="membership-form__error-message">{errors['emergencyContact.barangay']}</span>}
                </label>
                <label className={`membership-form__field ${errors['emergencyContact.postalCode'] ? "membership-form__field--error" : ""}`}>
                  <span>Postal Code *</span>
                  <input name="postalCode" value={form.emergencyContact.postalCode} onChange={handleEmergencyChange} required readOnly={useSameAddress} disabled={useSameAddress} />
                  {errors['emergencyContact.postalCode'] && <span className="membership-form__error-message">{errors['emergencyContact.postalCode']}</span>}
                </label>
            </div>

            <label className={`membership-form__field ${errors['emergencyContact.address1'] ? "membership-form__field--error" : ""}`}>
              <span>Address Line 1 *</span>
              <input name="address1" value={form.emergencyContact.address1} onChange={handleEmergencyChange} placeholder="House/Unit/Block No., Street, Subdivision/Village" required readOnly={useSameAddress} disabled={useSameAddress} />
              {errors['emergencyContact.address1'] && <span className="membership-form__error-message">{errors['emergencyContact.address1']}</span>}
            </label>
          </FormSection>
          
          {/* Step 4: Photo Upload */}
          <FormSection title="Profile Photo" isActive={currentStep === 4}>
            <div className="photo-upload-container">
              {photoCropSource ? (
                <div className="photo-crop-editor">
                  <div
                    ref={photoCropperStageRef}
                    className="photo-cropper-stage"
                    onPointerDown={() => setIsPhotoCropInteracting(true)}
                    onPointerCancel={() => setIsPhotoCropInteracting(false)}
                  >
                    <Cropper
                      image={photoCropSource}
                      crop={photoCrop}
                      cropSize={photoCropSize}
                      zoom={photoZoom}
                      minZoom={photoMinZoom}
                      aspect={1}
                      cropShape="rect"
                      objectFit="cover"
                      restrictPosition
                      showGrid={false}
                      onCropChange={setPhotoCrop}
                      onZoomChange={setPhotoZoom}
                      onCropComplete={handlePhotoCropComplete}
                      onMediaLoaded={handlePhotoMediaLoaded}
                    />
                    <div
                      className={`photo-crop-grid-guide${isPhotoCropInteracting ? " is-visible" : ""}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="photo-crop-controls">
                    <label className="photo-crop-zoom-field">
                      <span>Zoom</span>
                      <input
                        type="range"
                        min={photoMinZoom}
                        max={photoMaxZoom}
                        step={0.01}
                        value={photoZoom}
                        onChange={(event) => setPhotoZoom(Number(event.target.value))}
                      />
                    </label>
                    <div className="photo-crop-action-row">
                      <button
                        type="button"
                        className="button-primary"
                        onClick={applyPhotoCrop}
                        disabled={isApplyingPhotoCrop || isValidatingPhotoHuman}
                      >
                        {isValidatingPhotoHuman ? "Validating..." : (isApplyingPhotoCrop ? "Applying..." : "Apply Crop")}
                      </button>
                      <button
                        type="button"
                        className="button-secondary"
                        onClick={() => {
                          setPhotoCropSource(null);
                          setIsPhotoCropInteracting(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
              <div className={`photo-preview ${profilePreview ? 'has-photo' : ''}`}>
                {profilePreview ? (
                   <img src={profilePreview} alt="Preview" />
                ) : (
                  <div className="photo-placeholder">
                    <ImageIcon size={48} />
                    <span>2x2 Photo</span>
                  </div>
                )}
              </div>
              )}
              <div className="photo-actions">
                <p>Please upload then adjust your photo. The system saves it as a square 1:1 ID photo.</p>
                <label className="button-primary">
                  <Upload size={16} />
                  {photoCropSource ? "Choose Different Photo" : "Choose Photo"}
                  <input type="file" hidden accept="image/*" onChange={handlePhotoUpload} />
                </label>
                {photoCropError && <span className="membership-form__error-message">{photoCropError}</span>}
                {profilePreview && (
                  <button type="button" className="button-outline" onClick={() => {
                    setPhotoCropSource(null);
                    setPhotoCropError("");
                    setIsPhotoCropInteracting(false);
                    setProfilePreview(null);
                    setForm(prev => ({ ...prev, profilePicture: null }));
                  }}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </FormSection>

           {/* Step 5: Digital Signature */}
           <FormSection title="Digital Signature" isActive={currentStep === 5}>
            <div className="signature-section">
              <p className="helper-text">Please sign below to confirm your application. This will be used as your official digital signature.</p>
              {readOnly && signature ? (
                <div className="signature-pad-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px' }}>
                  <img src={signature} alt="Saved Signature" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} />
                </div>
              ) : (
                <SignaturePad onSave={(data) => setSignature(data)} disabled={readOnly} />
              )}
            </div>
           </FormSection>

           <FormSection title="Review & Confirm" isActive={currentStep === 6}>
            <div style={{ border: '1px solid #dbeafe', borderRadius: '10px', overflow: 'hidden', marginBottom: '14px' }}>
              <div style={{ background: '#0ea5e9', color: '#fff', fontWeight: 700, letterSpacing: '0.08em', padding: '9px 12px' }}>PERSONAL INFORMATION</div>
              <div style={{ padding: '10px 12px', display: 'grid', gap: '8px' }}>
                  {renderReviewRow("Full Name", `${form.firstName} ${form.middleInitial} ${form.lastName}`.replace(/\s+/g, ' '))}
                  {renderReviewRow("Place of Birth", form.placeOfBirth)}
                  {renderReviewRow("Date of Birth", toIsoDate(form.dateOfBirth))}
                  {renderReviewRow("Address", `${form.address1}, ${form.barangay}, ${form.city}, ${form.province}, ${form.postalCode}`)}
                  {renderReviewRow("Status", form.maritalStatus)}
                  {renderReviewRow("Educational Attainment", form.education)}
                  {renderReviewRow("No. of Children", form.numberOfChildren)}
                  {renderReviewRow("Religion", form.religion === "Others" ? form.otherReligion : form.religion)}
                  {renderReviewRow("Contact No.", form.phone)}
                  {renderReviewRow("E-Mail", form.email)}
                  {renderReviewRow("Gender", form.gender === "Other" ? form.otherGender : form.gender)}
              </div>
            </div>

            <div style={{ border: '1px solid #dbeafe', borderRadius: '10px', overflow: 'hidden', marginBottom: '14px' }}>
              <div style={{ background: '#0ea5e9', color: '#fff', fontWeight: 700, letterSpacing: '0.08em', padding: '9px 12px' }}>EMPLOYMENT INFORMATION</div>
              <div style={{ padding: '10px 12px', display: 'grid', gap: '8px' }}>
                {renderReviewRow("Company", form.company)}
                {renderReviewRow("Position", form.position)}
                {renderReviewRow("Union Name", form.department)}
                {renderReviewRow("Years Employed", formatYearsEmployed(form.yearsEmployed))}
                {renderReviewRow("Union Affiliation", form.unionAffiliation === "Others" ? form.otherUnionAffiliation : form.unionAffiliation)}
                {renderReviewRow("Position in Union", form.unionPosition)}
              </div>
            </div>

            <div style={{ border: '1px solid #dbeafe', borderRadius: '10px', overflow: 'hidden', marginBottom: '14px' }}>
              <div style={{ background: '#0ea5e9', color: '#fff', fontWeight: 700, letterSpacing: '0.08em', padding: '9px 12px' }}>CONTACT PERSON IN CASE OF EMERGENCY</div>
              <div style={{ padding: '10px 12px', display: 'grid', gap: '8px' }}>
                {renderReviewRow("Full Name", form.emergencyContact.name)}
                {renderReviewRow("Address", form.emergencyContact.address)}
                {renderReviewRow("Contact No.", form.emergencyContact.phone)}
                {renderReviewRow("Relationship", form.emergencyContact.relationship)}
              </div>
            </div>

            <div className="membership-form__fields membership-form__fields--cols-2" style={{ marginBottom: '12px' }}>
              <div className="membership-form__field">
                <span>ID Photo Preview</span>
                {profilePreview ? (
                  <img src={profilePreview} alt="ID Preview" style={{ width: '110px', height: '110px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                ) : (
                  <div className="admin-muted">No photo uploaded</div>
                )}
              </div>
              <div className="membership-form__field">
                <span>Signature Preview</span>
                {hasValidSignature(signature) ? (
                  <div className="signature-review-frame">
                    <img
                      src={signature}
                      alt="Signature Preview"
                      className={`signature-review-image${signaturePreviewIsPortrait ? " is-portrait" : ""}`}
                      onLoad={(event) => {
                        const { naturalWidth, naturalHeight } = event.currentTarget;
                        setSignaturePreviewIsPortrait(naturalHeight > naturalWidth);
                      }}
                    />
                  </div>
                ) : (
                  <div className="admin-muted">No signature provided</div>
                )}
              </div>
            </div>

            <label className={`membership-form__privacy-consent ${errors.privacyConsent ? "membership-form__field--error" : ""}`}>
              <input
                type="checkbox"
                checked={readOnly ? true : privacyConfirmed}
                onChange={(event) => {
                  setPrivacyConfirmed(event.target.checked);
                  if (errors.privacyConsent) {
                    setErrors((prev) => ({ ...prev, privacyConsent: null }));
                  }
                }}
              />
              <span className="membership-form__privacy-consent-text">
                I confirm that all information above is correct, and I agree to the processing of my personal data in accordance with the Data Privacy Act of 2012 and LaborConnect's {" "}
                <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="membership-form__privacy-consent-link">
                  Data Privacy Policy
                </Link>
                {" "}and{" "}
                <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="membership-form__privacy-consent-link">
                  Terms &amp; Conditions
                </Link>
                .
              </span>
            </label>
            {errors.privacyConsent && <span className="membership-form__error-message">{errors.privacyConsent}</span>}
           </FormSection>
          </fieldset>

          {/* Navigation Buttons */}
          <div className="step-actions">
             {currentStep > 1 && (
               <button type="button" className="button-outline" onClick={handleBackStep}>
                 Back
               </button>
             )}
             
             {currentStep < steps.length ? (
               <button 
                 type="button" 
                 className="button-primary" 
                 onClick={handleNext} 
                 disabled={readOnly ? false : (!isStepValid || (currentStep === 4 && (isApplyingPhotoCrop || isValidatingPhotoHuman)))}
               >
                 {currentStep === 5 ? "Review & Confirm" : "Next Step"} <ArrowRight size={16} />
               </button>
             ) : (
               readOnly ? (
                <button type="button" className="button-primary" onClick={onBack}>
                  Back to Dashboard
                </button>
               ) : (
                <button type="submit" className="button-primary" disabled={submitting || !isStepValid || !hasValidSignature(signature)}>
                  {submitting ? "Processing..." : "Submit Application"}
                </button>
               )
             )}
          </div>

        </form>
      </div>
     </div> {/* End of card */}
     
     <footer className="membership-form__footer" style={{ marginTop: 'auto' }}>
        Need assistance? Contact LaborConnect at <strong>(02) 8123-4567</strong> or visit our office during business hours.
     </footer>

      {/* Confirmation Modal */}
      {showBackWarning && (
        <div className="layout-confirm" role="dialog" aria-modal="true" aria-labelledby="back-warning-title">
          <div className="layout-confirm__backdrop" onClick={() => setShowBackWarning(false)} />
          <div className="layout-confirm__panel">
            <h3 id="back-warning-title">Are you sure you want to go back?</h3>
            <p>Any details you have entered in the form will not be saved.</p>
            <div className="layout-confirm__actions">
              <button
                type="button"
                className="layout-confirm__button"
                onClick={() => setShowBackWarning(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="layout-confirm__button layout-confirm__button--danger"
                onClick={onBack}
              >
                Yes, go back
              </button>
            </div>
          </div>
        </div>
      )}

    </div> {/* End of container */}
   </div> /* End of membership-form */
  );
}

RegistrationPage.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  submitError: PropTypes.string,
  initialData: PropTypes.object,
  readOnly: PropTypes.bool,
  initialStep: PropTypes.number,
};

RegistrationPage.defaultProps = {
  submitting: false,
  submitError: "",
  initialData: null,
  readOnly: false,
  initialStep: 1,
};
